import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useClientConfig } from 'src/hooks/useClientConfig';
import { AppMode } from 'src/store/actions/appMode';
import { useAppSelector } from 'universal-scripts';
import Analytics from './Analytics';

const Meta = () => {
	const intl = useIntl();
	const client = useClientConfig();
	const appMode = useAppSelector((s) => s.appMode as AppMode);

	if (appMode !== 'standalone') return null;

	const title = client?.metaTitle ?? intl.formatMessage({ id: 'meta.title' });
	const ogTitle = client?.metaTitle ?? intl.formatMessage({ id: 'meta.og-title' });
	const description = client?.metaDescription ?? intl.formatMessage({ id: 'meta.description' });
	const keywords = client?.metaKeywords ?? intl.formatMessage({ id: 'meta.keywords' });
	const ogImage = client?.ogImageUrl ?? '/og/og-image.png';
	const ogUrl = client?.canonicalUrl ?? 'https://chatbot.trueflag.ai';
	const favicon = client?.faviconUrl ?? '/favicon.ico';

	return (
		<>
			<Helmet>
				<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, viewport-fit=cover, user-scalable=no" />
				<meta name="description" content={description} />
				<meta name="keywords" content={keywords} />
				<title>{title}</title>
				{__PROD__ || (__DEV__ && <meta httpEquiv="Content-Security-Policy" content="upgrade-insecure-requests" />)}

				<meta property="og:type" content="website" />
				<meta property="og:title" content={ogTitle} />
				<meta property="og:url" content={ogUrl} />
				<meta property="og:image" content={ogImage} />
				<meta property="og:image:width" content="1200" />
				<meta property="og:image:height" content="630" />
				<meta property="og:description" content={description} />

				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={title} />
				<meta name="twitter:description" content={description} />
				<meta name="twitter:image" content={ogImage} />

				<link rel="shortcut icon" href={favicon} />
				<link rel="manifest" href="/manifest.json" />
				<script src="/env-config.js"></script>
				<script src="/version.js"></script>
				<link rel="icon" type="image/png" sizes="16x16" href="/pwa/ios/16.png" />
				<link rel="icon" type="image/png" sizes="32x32" href="/pwa/ios/32.png" />
				<meta name="apple-mobile-web-app-capable" content="yes" />
				<meta name="apple-mobile-web-app-status-bar-style" content="default" />
				<meta name="apple-mobile-web-app-title" content={title} />
				<link
					href="/pwa/splashscreens/iphone5_splash.png"
					media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2)"
					rel="apple-touch-startup-image"
				/>
				<link
					href="/pwa/splashscreens/iphone6_splash.png"
					media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2)"
					rel="apple-touch-startup-image"
				/>
				<link
					href="/pwa/splashscreens/iphoneplus_splash.png"
					media="(device-width: 621px) and (device-height: 1104px) and (-webkit-device-pixel-ratio: 3)"
					rel="apple-touch-startup-image"
				/>
				<link
					href="/pwa/splashscreens/iphonex_splash.png"
					media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3)"
					rel="apple-touch-startup-image"
				/>
				<link
					href="/pwa/splashscreens/iphonexr_splash.png"
					media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2)"
					rel="apple-touch-startup-image"
				/>
				<link
					href="/pwa/splashscreens/iphonexsmax_splash.png"
					media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3)"
					rel="apple-touch-startup-image"
				/>
				<link
					href="/pwa/splashscreens/ipad_splash.png"
					media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2)"
					rel="apple-touch-startup-image"
				/>
				<link
					href="/pwa/splashscreens/ipadpro1_splash.png"
					media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2)"
					rel="apple-touch-startup-image"
				/>
				<link
					href="/pwa/splashscreens/ipadpro3_splash.png"
					media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2)"
					rel="apple-touch-startup-image"
				/>
				<link
					href="/pwa/splashscreens/ipadpro2_splash.png"
					media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2)"
					rel="apple-touch-startup-image"
				/>
				<link rel="apple-touch-icon" sizes="180x180" href="/pwa/apple-touch-icon.png" />
				<meta name="msapplication-TileColor" content="#2B2B2B" />
				<meta name="theme-color" content="#2B2B2B" />
				<script>
					{
						'if ("serviceWorker" in navigator) { window.addEventListener("load", () => {  navigator.serviceWorker.register("/sw.js", {scope: "/"}).then(registration => { console.log("Service Worker registered: ", registration) }).catch(registrationError => { console.log("Service Worker registration failed: ", registrationError) }) }) }'
					}
				</script>
			</Helmet>
			<Analytics />
		</>
	);
};

export default Meta;
